import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Used for both /404 (page not found) and /unauthorized (access denied)
export default function NotFoundPage({ mode = "notfound" }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-detect mode from pathname if not passed as prop
  const isUnauthorized =
    mode === "unauthorized" || location.pathname === "/unauthorized";

  const [count, setCount] = useState(10);

  useEffect(() => {
    const timer = setInterval(() => {
      setCount((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(isUnauthorized ? "/" : "/");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate, isUnauthorized]);

  const content = isUnauthorized
    ? {
        code: "403",
        heading: "Access Denied",
        subtext:
          "You don't have permission to view this page. Contact your Admin if you think this is a mistake.",
        redirectLabel: "Back to Dashboard",
        redirectPath: "/",
      }
    : {
        code: "404",
        heading: "Page Not Found",
        subtext:
          "The page you're looking for doesn't exist or has been moved.",
        redirectLabel: "Go to Dashboard",
        redirectPath: "/",
      };

  return (
    <div style={styles.wrapper}>
      <div style={styles.gridOverlay} />

      <div style={styles.card}>
        <div style={styles.accentBar} />

        {/* Code number */}
        <div style={styles.bigNumber}>{content.code}</div>

        {/* Icon */}
        <div style={styles.iconWrap}>
          {isUnauthorized ? (
            // Lock icon for 403
            <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
              <circle cx="26" cy="26" r="25" stroke="#0f6e56" strokeWidth="2" strokeDasharray="6 4" />
              <rect x="16" y="24" width="20" height="14" rx="3" stroke="#0f6e56" strokeWidth="2.2" />
              <path d="M20 24v-5a6 6 0 0112 0v5" stroke="#0f6e56" strokeWidth="2.2" strokeLinecap="round" />
              <circle cx="26" cy="31" r="2" fill="#0f6e56" />
            </svg>
          ) : (
            // X icon for 404
            <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
              <circle cx="26" cy="26" r="25" stroke="#0f6e56" strokeWidth="2" strokeDasharray="6 4" />
              <circle cx="26" cy="26" r="4" fill="#0f6e56" opacity="0.15" />
              <path d="M22 22l8 8M30 22l-8 8" stroke="#0f6e56" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          )}
        </div>

        <h1 style={styles.heading}>{content.heading}</h1>
        <p style={styles.subtext}>{content.subtext}</p>

        {/* Auto-redirect notice */}
        <div style={styles.redirectBox}>
          <span style={styles.dot} />
          Redirecting in <span style={styles.countBadge}>{count}s</span>
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          <button
            style={styles.btnPrimary}
            onClick={() => navigate(content.redirectPath)}
          >
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l9-9 9 9M5 10v9a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-9" />
            </svg>
            {content.redirectLabel}
          </button>
          <button style={styles.btnOutline} onClick={() => navigate(-1)}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Go Back
          </button>
        </div>
      </div>

      <div style={styles.brand}>Staff Management System</div>
    </div>
  );
}

const styles = {
  wrapper: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0a1f1c 0%, #0d2b26 50%, #0a1f1c 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    position: "relative",
    overflow: "hidden",
    padding: "20px",
  },
  gridOverlay: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(15,110,86,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(15,110,86,0.07) 1px, transparent 1px)",
    backgroundSize: "40px 40px",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(15,110,86,0.25)",
    borderRadius: 20,
    padding: "48px 40px 40px",
    width: "100%",
    maxWidth: 460,
    textAlign: "center",
    backdropFilter: "blur(12px)",
    boxShadow: "0 8px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(15,110,86,0.1) inset",
  },
  accentBar: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: 80,
    height: 3,
    background: "linear-gradient(90deg, #085041, #0f6e56, #085041)",
    borderRadius: "0 0 4px 4px",
  },
  bigNumber: {
    fontSize: 96,
    fontWeight: 800,
    lineHeight: 1,
    background: "linear-gradient(135deg, #0f6e56 0%, #1a9b7a 50%, #0f6e56 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    letterSpacing: "-4px",
    marginBottom: 16,
    userSelect: "none",
  },
  iconWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 20,
    opacity: 0.9,
  },
  heading: {
    fontSize: 22,
    fontWeight: 700,
    color: "#f0faf7",
    margin: "0 0 10px",
    letterSpacing: "-0.3px",
  },
  subtext: {
    fontSize: 14,
    color: "rgba(240,250,247,0.5)",
    lineHeight: 1.6,
    margin: "0 0 24px",
  },
  redirectBox: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(15,110,86,0.12)",
    border: "1px solid rgba(15,110,86,0.2)",
    borderRadius: 30,
    padding: "8px 16px",
    fontSize: 13,
    color: "rgba(240,250,247,0.65)",
    marginBottom: 28,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#0f6e56",
    display: "inline-block",
  },
  countBadge: {
    fontWeight: 700,
    color: "#1a9b7a",
    fontSize: 14,
    minWidth: 24,
    display: "inline-block",
  },
  actions: {
    display: "flex",
    gap: 12,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "linear-gradient(135deg, #085041, #0f6e56)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "11px 22px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(8,80,65,0.4)",
  },
  btnOutline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "transparent",
    color: "rgba(240,250,247,0.7)",
    border: "1px solid rgba(15,110,86,0.3)",
    borderRadius: 10,
    padding: "11px 22px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  brand: {
    marginTop: 32,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "rgba(15,110,86,0.4)",
  },
};